import { registerValidator } from '../validatorRegistry';
import type { ValidationContext, ValidationResult } from '../types';

const SURFACE_PRINT_INCHES: Record<string, { w: number; h: number }> = {
  front: { w: 11, h: 15 }, back: { w: 11, h: 15 },
  leftSleeve: { w: 3.75, h: 5 }, rightSleeve: { w: 3.75, h: 5 },
  collar: { w: 5, h: 2.5 },
};

const grade = (key: string, dpi: number, recW: number, recH: number): ValidationResult => {
  if (dpi >= 300) return { key, status: 'pass', blocker: false, title: 'Resolution OK', message: `~${Math.round(dpi)} DPI. Good for print.` };
  if (dpi >= 150) return { key, status: 'warning', blocker: false, title: 'Medium resolution', message: `~${Math.round(dpi)} DPI. May appear slightly soft when printed.`, suggestion: `Upload at least ${recW}×${recH}px for best results.` };
  return { key, status: 'warning', blocker: false, title: 'Low resolution', message: `~${Math.round(dpi)} DPI. Will likely appear blurry when printed.`, suggestion: `Upload at least ${recW}×${recH}px for best results.` };
};

registerValidator({
  key: 'resolution',
  runsOn: ['upload', 'placement', 'scale'],
  async validate(ctx: ValidationContext) {
    // SVGs are vector — always high DPI
    if (ctx.file?.type === 'image/svg+xml') {
      return { key: 'resolution', status: 'pass', blocker: false, title: 'Vector — High DPI', message: 'SVG is resolution independent — prints at maximum quality at any size.' };
    }

    // Upload stage — evaluate against largest surface
    if (!ctx.node && ctx.imageElement) {
      const pw = ctx.imageElement.naturalWidth;
      const ph = ctx.imageElement.naturalHeight;
      let maxW = 11, maxH = 15;
      if (ctx.productConfig) {
        for (const s of ctx.productConfig.surfaces) {
          const d = SURFACE_PRINT_INCHES[s];
          if (d && d.w * d.h > maxW * maxH) { maxW = d.w; maxH = d.h; }
        }
      }
      const dpi = Math.min(pw / maxW, ph / maxH);
      return grade('resolution', dpi, Math.round(maxW * 300), Math.round(maxH * 300));
    }

    // Placement / scale stage — evaluate against placed rect
    if (ctx.node && ctx.placedRect && ctx.surface && ctx.canvasDimensions) {
      const si = SURFACE_PRINT_INCHES[ctx.surface] ?? { w: 11, h: 15 };
      const pxPerInchX = ctx.canvasDimensions.width / si.w;
      const pxPerInchY = ctx.canvasDimensions.height / si.h;
      const placedW = ctx.placedRect.width / pxPerInchX;
      const placedH = ctx.placedRect.height / pxPerInchY;
      const origW = ctx.imageElement?.naturalWidth ?? ctx.node.size.width;
      const origH = ctx.imageElement?.naturalHeight ?? ctx.node.size.height;
      const dpi = Math.min(origW / placedW, origH / placedH);
      return grade('resolution', dpi, Math.round(placedW * 300), Math.round(placedH * 300));
    }

    return { key: 'resolution', status: 'pass', blocker: false, title: 'Resolution', message: '' };
  },
});
